import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, KeyRound, Check, X } from "lucide-react";
import { toast } from "sonner";

const MODEL_FIELDS = [
  ["default_model", "Model implicit", "ollama:qwen2.5:7b"],
  ["fast_model", "Model rapid (rutare, memorie, tool-uri)", "ollama:llama3.2:3b"],
  ["smart_model", "Model „smart” (/atena smart)", "claude:claude-sonnet-4-6"],
  ["gpt_model", "Model GPT (/atena gpt)", "openai:gpt-4o-mini"],
];

const TOGGLES = [
  ["enabled", "Atena activă", "Oprită, endpoint-ul refuză orice cerere din acest workspace."],
  ["allow_post", "Poate posta în canale", "Tool-ul post_message. Oprit, Atena răspunde dar nu scrie nicăieri."],
  ["allow_ticket", "Poate actualiza tichete", "Chiar și pornit, orice schimbare de status cere confirmarea ta."],
  ["allow_dictation", "Permite dictarea prin voce",
   "Dictarea folosește recunoașterea de voce a browserului — pe Chrome și Edge audio-ul ajunge la serverele vendorului. Oprită, microfonul dispare din panou pentru tot workspace-ul. Citirea răspunsurilor cu voce rămâne locală și nu e afectată."],
];

function Toggle({ id, checked, onChange, label, hint }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button" role="switch" aria-checked={checked} aria-label={label}
        data-testid={`atena-toggle-${id}`} onClick={() => onChange(!checked)}
        className={`mt-0.5 flex-shrink-0 w-10 h-5 border transition-colors ${
          checked ? "bg-brand/30 border-brand/50" : "bg-surface-2 border-line/20"}`}
      >
        <span className={`block w-4 h-4 mt-px transition-transform ${
          checked ? "translate-x-5 bg-brand" : "translate-x-px bg-fg/40"}`} />
      </button>
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-fg-muted leading-relaxed">{hint}</p>
      </div>
    </div>
  );
}

export default function AtenaSettings({ workspaceId }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    api.get("/atena/settings", { params: { workspace_id: workspaceId } })
      .then((r) => setCfg(r.data))
      .catch(() => setCfg(null));
  }, [workspaceId]);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/atena/settings", payload,
        { params: { workspace_id: workspaceId } });
      setCfg(data);
      toast.success("Setări Atena salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu s-a putut salva");
    } finally { setSaving(false); }
  };

  if (!cfg) return null;

  return (
    <section className="bg-surface border border-line/10 mt-6" data-testid="atena-settings">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Atena</h3>
        <span className="label-tiny ml-2">// ASISTENT AI</span>
      </div>

      <div className="p-5 space-y-5">
        <div className="space-y-4">
          {TOGGLES.map(([key, label, hint]) => (
            <Toggle
              key={key} id={key} label={label} hint={hint}
              checked={Boolean(cfg[key])}
              onChange={(v) => patch({ [key]: v })}
            />
          ))}
        </div>

        <div className="pt-4 border-t border-line/10 space-y-3">
          <p className="label-tiny">// MODELE</p>
          {MODEL_FIELDS.map(([key, label, placeholder]) => (
            <div key={key}>
              <label htmlFor={`atena-${key}`} className="label-tiny block mb-1.5">{label}</label>
              <input
                id={`atena-${key}`} data-testid={`atena-model-${key}`}
                defaultValue={cfg[key] || ""} placeholder={placeholder}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (cfg[key] || "")) patch({ [key]: v });
                }}
                disabled={saving}
                className="input-flat w-full mono text-xs"
              />
            </div>
          ))}
          <p className="text-[11px] text-fg-muted">
            Format <span className="mono">provider:model</span> — provider poate fi
            <span className="mono"> ollama</span>, <span className="mono">openai</span> sau
            <span className="mono"> claude</span>. Gol înseamnă valoarea din environment.
          </p>
        </div>

        <div className="pt-4 border-t border-line/10">
          <p className="label-tiny mb-2">// CHEI PROVIDER</p>
          <ul className="space-y-1.5">
            {[["OPENAI_API_KEY", cfg.openai_key_configured], ["ANTHROPIC_API_KEY", cfg.anthropic_key_configured]].map(
              ([name, ok]) => (
                <li key={name} className="flex items-center gap-2 text-xs">
                  <KeyRound className="w-3.5 h-3.5 text-fg-muted flex-shrink-0" />
                  <span className="mono flex-1">{name}</span>
                  {ok
                    ? <span className="flex items-center gap-1 text-brand"><Check className="w-3.5 h-3.5" /> configurată</span>
                    : <span className="flex items-center gap-1 text-fg-muted"><X className="w-3.5 h-3.5" /> lipsește</span>}
                </li>
              ))}
          </ul>
          <p className="text-[11px] text-fg-muted mt-2 leading-relaxed">
            Cheile se citesc din environment și nu pot fi setate de aici — o cheie salvată în
            baza de date ar ajunge în fiecare backup. Se adaugă în fișierul de environment al
            serverului, iar valoarea nu e afișată niciodată înapoi.
          </p>
        </div>
      </div>
    </section>
  );
}
